import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { platformApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, ArrowLeft, AlertTriangle, Pause, Play, KeyRound, Save, Snowflake, Sun, Send, CalendarPlus, Percent, Euro, X, BadgePercent, Users, Briefcase } from "lucide-react";
import { usePlanDisplay, priceInline, VatFootnote } from "@/lib/plan";

// ------- Phase 12: status badge helper -------
function StatusBadge({ status, archived }) {
  if (archived) {
    return <span className="text-[10px] mono uppercase bg-rose-900/50 text-rose-300 border border-rose-800 px-1.5 py-0.5 rounded">Suspended</span>;
  }
  const map = {
    active:  { cls: "bg-emerald-900/40 text-emerald-300 border-emerald-800", label: "Active" },
    grace:   { cls: "bg-amber-900/40 text-amber-300 border-amber-800",       label: "Grace" },
    frozen:  { cls: "bg-sky-900/40 text-sky-300 border-sky-800",             label: "Frozen" },
  };
  const m = map[status] || map.active;
  return <span className={`text-[10px] mono uppercase border px-1.5 py-0.5 rounded ${m.cls}`} data-testid={`platform-status-badge-${status}`}>{m.label}</span>;
}

const FILTERS = [
  { key: "all",       label: "All" },
  { key: "active",    label: "Active" },
  { key: "grace",     label: "Grace" },
  { key: "frozen",    label: "Frozen" },
  { key: "suspended", label: "Suspended" },
];

export function AdminCompaniesList() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await platformApi.get("/platform/companies");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const matchesFilter = (r) => {
    if (filter === "all") return true;
    if (filter === "suspended") return !!r.archived;
    if (r.archived) return false;
    return (r.subscription_status || "active") === filter;
  };
  const filtered = rows.filter((r) => matchesFilter(r) && (!q || r.name.toLowerCase().includes(q.toLowerCase()) || r.owner_email.toLowerCase().includes(q.toLowerCase())));

  const setSuspend = async (c, suspend) => {
    try {
      const path = suspend ? "suspend" : "unsuspend";
      await platformApi.post(`/platform/companies/${c.id}/${path}`);
      toast.success(`${c.name} ${suspend ? "suspended" : "unsuspended"}`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const softDelete = async (c) => {
    if (!confirm(`Soft-delete ${c.name}? Data is retained ~90 days for recovery.`)) return;
    try {
      await platformApi.delete(`/platform/companies/${c.id}`);
      toast.success(`${c.name} archived`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-companies-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-rose-400">// Tenants</div>
          <h1 className="text-3xl font-black mt-1">Companies</h1>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-rose-600 hover:bg-rose-700" data-testid="platform-company-create">
          <Plus className="h-4 w-4 mr-1" /> New company
        </Button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-[8px] p-4 space-y-3">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or owner email…" className="pl-9 bg-slate-800 border-slate-700 text-slate-100" data-testid="admin-companies-search" />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="admin-companies-filter-chips">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded border transition-colors ${filter === f.key ? "bg-rose-600 border-rose-500 text-white" : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"}`}
              data-testid={`admin-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <section className="bg-slate-900 border border-slate-800 rounded-[8px] overflow-x-auto">
        <table className="w-full text-sm" data-testid="platform-companies-table">
          <thead className="bg-slate-950/50 text-slate-400">
            <tr>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Owner</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2" data-testid="admin-th-stripe-connect">Stripe</th>
              <th className="text-left px-4 py-2">Expires</th>
              <th className="text-right px-4 py-2">Mgrs</th>
              <th className="text-right px-4 py-2">Bookings</th>
              <th className="text-right px-4 py-2">Invoices</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-slate-800" data-testid={`platform-company-row-${c.id}`}>
                <td className="px-4 py-2 font-bold">
                  {c.name}
                  {c.billing_cycle && (
                    <span className="ml-2 mono text-[10px] uppercase bg-sky-900/40 text-sky-300 border border-sky-800 px-1.5 py-0.5 rounded" data-testid={`platform-company-cycle-${c.id}`}>
                      {c.billing_cycle}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 mono text-xs text-slate-400">{c.owner_email}</td>
                <td className="px-4 py-2"><StatusBadge status={c.subscription_status} archived={c.archived} /></td>
                <td className="px-4 py-2" data-testid={`admin-stripe-connect-${c.id}`}>
                  {(() => {
                    const cs = c.stripe_connect_status || "not_connected";
                    const cls = {
                      connected: "bg-emerald-700/30 text-emerald-300 border-emerald-700/50",
                      pending: "bg-amber-700/30 text-amber-300 border-amber-700/50",
                      disconnected: "bg-zinc-700/30 text-zinc-300 border-zinc-700/50",
                      rejected: "bg-rose-700/30 text-rose-300 border-rose-700/50",
                      not_connected: "bg-zinc-800/40 text-zinc-500 border-zinc-700/50",
                    }[cs] || "bg-zinc-800/40 text-zinc-500 border-zinc-700/50";
                    return (
                      <span className={`inline-flex items-center gap-1 mono uppercase tracking-wider text-[10px] font-bold rounded-full border px-2 py-0.5 ${cls}`}>
                        {cs.replace("_", " ")}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-4 py-2 mono text-xs text-slate-400" data-testid={`platform-company-expires-${c.id}`}>
                  {c.subscription_expires_at ? new Date(c.subscription_expires_at).toISOString().slice(0, 10) : <span className="text-slate-600">—</span>}
                  {!c.archived && c.subscription_expires_at && (
                    <span className={`ml-2 text-[10px] ${c.days_to_expiry < 0 ? "text-rose-400" : c.days_to_expiry <= 7 ? "text-amber-400" : "text-slate-500"}`}>
                      ({c.days_to_expiry > 0 ? `${c.days_to_expiry}d` : c.days_to_expiry === 0 ? "today" : `${Math.abs(c.days_to_expiry)}d ago`})
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right mono">{c.manager_count}</td>
                <td className="px-4 py-2 text-right mono">{c.booking_count}</td>
                <td className="px-4 py-2 text-right mono">{c.invoice_count}</td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button variant="outline" size="sm" className="border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-200" onClick={() => nav(`/admin/companies/${c.id}`)}>View</Button>
                    {c.archived ? (
                      <Button variant="outline" size="sm" className="border-emerald-700 bg-emerald-900/30 hover:bg-emerald-900/60 text-emerald-200" onClick={() => setSuspend(c, false)} data-testid={`platform-company-unsuspend-${c.id}`}><Play className="h-3 w-3" /></Button>
                    ) : (
                      <Button variant="outline" size="sm" className="border-amber-700 bg-amber-900/30 hover:bg-amber-900/60 text-amber-200" onClick={() => setSuspend(c, true)} data-testid={`platform-company-suspend-${c.id}`}><Pause className="h-3 w-3" /></Button>
                    )}
                    <Button variant="outline" size="sm" className="border-rose-800 bg-rose-950/40 hover:bg-rose-900/60 text-rose-300" onClick={() => softDelete(c)} data-testid={`platform-company-delete-${c.id}`}><AlertTriangle className="h-3 w-3" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-500">No companies match.</td></tr>}
          </tbody>
        </table>
      </section>

      <CreateCompanyDialog open={creating} onOpenChange={setCreating} onCreated={load} />
    </div>
  );
}

function CreateCompanyDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", initial_manager_email: "", initial_manager_password: "", initial_manager_name: "", trial_days: 30 });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, trial_days: form.trial_days ? Number(form.trial_days) : 30 };
      await platformApi.post("/platform/companies", payload);
      toast.success(`Created ${form.name}`);
      onOpenChange(false);
      setForm({ name: "", initial_manager_email: "", initial_manager_password: "", initial_manager_name: "", trial_days: 30 });
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="admin-create-company-dialog">
        <DialogHeader>
          <DialogTitle>Create new company</DialogTitle>
          <DialogDescription className="text-slate-400">Provisions a tenant + initial manager + baseline templates and counters.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-[10px] uppercase tracking-wider text-slate-400">Company name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="admin-create-company-name" /></div>
          <div><Label className="text-[10px] uppercase tracking-wider text-slate-400">Initial manager email *</Label>
            <Input value={form.initial_manager_email} onChange={(e) => setForm({ ...form, initial_manager_email: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="admin-create-company-email" /></div>
          <div><Label className="text-[10px] uppercase tracking-wider text-slate-400">Initial manager password *</Label>
            <Input type="password" value={form.initial_manager_password} onChange={(e) => setForm({ ...form, initial_manager_password: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="admin-create-company-password" /></div>
          <div><Label className="text-[10px] uppercase tracking-wider text-slate-400">Initial manager name</Label>
            <Input value={form.initial_manager_name} onChange={(e) => setForm({ ...form, initial_manager_name: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="admin-create-company-mgr-name" /></div>
          <div><Label className="text-[10px] uppercase tracking-wider text-slate-400">Trial days (default 30)</Label>
            <Input type="number" min="0" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="admin-create-company-trial-days" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="admin-create-company-submit">{busy ? "Creating…" : "Create company"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REMINDER_LABELS = {
  "7d":       "T-7 (week before)",
  "0d":       "T-0 (expiry day)",
  "grace_5d": "T+5 (mid-grace)",
  "frozen":   "Frozen notice",
};

export function AdminCompanyDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState({ name: "", notes: "", subscription_plan: "free", subscription_expires_at: "" });
  const [resetPwd, setResetPwd] = useState("");
  const [resetting, setResetting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  // Phase O — plan_code Select + PLAN_LIMITS catalog + live utilization.
  const [planCode, setPlanCode] = useState("custom");
  const [savingPlan, setSavingPlan] = useState(false);
  const [planCatalog, setPlanCatalog] = useState(null);   // { basic: {...}, pro: {...}, ... }
  const [utilization, setUtilization] = useState(null);   // { staff_count, manager_total, ... }

  const load = async () => {
    try {
      const { data } = await platformApi.get(`/platform/companies/${id}`);
      setC(data);
      setEdit({
        name: data.name,
        notes: data.notes || "",
        subscription_plan: data.subscription_plan || "free",
        subscription_expires_at: data.subscription_expires_at || "",
      });
      setPlanCode((data.plan_code || "custom").toLowerCase());
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase O — fetch PLAN_LIMITS catalog + utilization once per company.
  useEffect(() => {
    (async () => {
      try {
        const [catRes, utilRes] = await Promise.all([
          platformApi.get("/platform/plan-limits"),
          platformApi.get(`/platform/companies/${id}/plan-utilization`),
        ]);
        setPlanCatalog(catRes.data || {});
        setUtilization(utilRes.data?.utilization || null);
      } catch (e) {
        // Non-fatal — the card just won't render.
      }
    })();
  }, [id]);

  const savePlan = async () => {
    setSavingPlan(true);
    try {
      await platformApi.post(`/platform/companies/${id}/set-plan`, { plan_code: planCode });
      toast.success(`Plan set to ${planCode}`);
      // Refresh both company + utilization so the tile reflects the new limits.
      const [_, utilRes] = await Promise.all([
        load(),
        platformApi.get(`/platform/companies/${id}/plan-utilization`),
      ]);
      setUtilization(utilRes.data?.utilization || null);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSavingPlan(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await platformApi.put(`/platform/companies/${id}`, edit);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!resetPwd || resetPwd.length < 6) { toast.error("Pick a password (≥6 chars)"); return; }
    setResetting(true);
    try {
      const { data } = await platformApi.post(`/platform/companies/${id}/reset-manager-password`, { new_password: resetPwd });
      toast.success(`Password reset for ${data.manager_email}`);
      setResetPwd("");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setResetting(false); }
  };

  // Phase 12 lifecycle actions
  const extendBy = async (days) => {
    setBusyAction(`extend-${days}`);
    try {
      await platformApi.post(`/platform/companies/${id}/extend-subscription`, { days });
      toast.success(`Extended subscription by ${days} days`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusyAction(""); }
  };
  const forceFreeze = async () => {
    if (!confirm(`Force-freeze ${c.name}? Manager + staff will lose access immediately.`)) return;
    setBusyAction("freeze");
    try {
      await platformApi.post(`/platform/companies/${id}/force-freeze`, { status: "frozen", reason: "Manual freeze by super-admin" });
      toast.success("Frozen");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusyAction(""); }
  };
  const forceUnfreeze = async () => {
    setBusyAction("unfreeze");
    try {
      await platformApi.post(`/platform/companies/${id}/force-unfreeze`, { status: "active", reason: "Manual unfreeze by super-admin" });
      toast.success("Unfrozen — status returns to date-driven computation");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusyAction(""); }
  };
  const sendReminderNow = async () => {
    setBusyAction("remind");
    try {
      const { data } = await platformApi.post(`/platform/companies/${id}/send-reminder-now`, {});
      if (data.sent) toast.success(`Sent ${data.code} to ${data.recipients?.length || 0} recipient(s)`);
      else toast.error(`Reminder not sent: ${data.reason || "unknown"}`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusyAction(""); }
  };

  const [subType, setSubType] = useState((c?.stripe_subscription_id) ? "payment" : "manual");
  const [onboardingBusy, setOnboardingBusy] = useState(false);

  const sendStripeOnboarding = async () => {
    setOnboardingBusy(true);
    try {
      const { data } = await platformApi.post(`/platform/companies/${id}/send-stripe-onboarding`);
      toast.success("Stripe onboarding email sent");
      if (data?.checkout_url) console.info("Onboarding URL:", data.checkout_url);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setOnboardingBusy(false); }
  };

  if (!c) return <div className="text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="admin-company-detail">
      <div className="flex items-center justify-between">
        <Link to="/admin/companies" className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Companies</Link>
        <div className="flex items-center gap-2">
          <StatusBadge status={c.subscription_status} archived={c.archived} />
          <Button onClick={save} disabled={saving} size="sm" className="bg-rose-600 hover:bg-rose-700 h-8 text-xs" data-testid="admin-company-save">
            <Save className="h-3 w-3 mr-1" />{saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <div>
        <div className="mono text-[9px] uppercase tracking-[0.3em] text-rose-400">// Tenant</div>
        <h1 className="text-xl font-black mt-0.5 leading-tight">{c.name}</h1>
        <div className="text-[11px] text-slate-400 mono">{c.owner_email} · created {(c.created_at || "").slice(0, 10)}</div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-2">
        <Stat label="Managers" value={c.manager_count} />
        <Stat label="Customers" value={c.customer_count} />
        <Stat label="Bookings" value={c.booking_count} />
        <Stat label="Invoices" value={c.invoice_count} />
      </div>

      {/* 2-column compact grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* LEFT COLUMN — Subscription */}
        <section className="bg-slate-900 border border-slate-800 rounded-[8px] p-3" data-testid="admin-subscription-panel">
          <div className="text-[10px] uppercase tracking-widest mono text-slate-400 flex items-center gap-1.5 mb-2">
            <Briefcase className="h-3.5 w-3.5" /> Plan &amp; subscription
          </div>
          <div className="flex items-center gap-2 mb-3" role="radiogroup" aria-label="Subscription type">
            {[
              { code: "payment", label: "Payment (Stripe)" },
              { code: "manual",  label: "Manual" },
            ].map((opt) => {
              const on = subType === opt.code;
              return (
                <button
                  key={opt.code}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setSubType(opt.code)}
                  className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${on ? "bg-accent/20 border-accent text-white" : "bg-slate-950 border-slate-800 text-slate-400 hover:text-slate-200"}`}
                  data-testid={`admin-subtype-${opt.code}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {subType === "manual" ? (
            <>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Expires on</Label>
              <Input
                type="date"
                value={(edit.subscription_expires_at || "").slice(0, 10)}
                onChange={(e) => setEdit({ ...edit, subscription_expires_at: e.target.value ? `${e.target.value}T00:00:00Z` : "" })}
                className="bg-slate-800 border-slate-700 h-8 text-sm mt-1"
                data-testid="admin-company-expires-input"
              />
              <div className="mt-2">
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">Plan code</Label>
                <Select value={planCode} onValueChange={setPlanCode}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1 h-8 text-xs" data-testid="admin-plan-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="pro_annual">Pro Annual</SelectItem>
                    <SelectItem value="pro_unlimited">Pro Unlimited</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={savePlan}
                  disabled={savingPlan || planCode === (c.plan_code || "custom").toLowerCase()}
                  size="sm" className="mt-2 bg-slate-700 hover:bg-slate-600 h-7 text-[11px] w-full" data-testid="admin-plan-save"
                >
                  {savingPlan ? "Saving…" : planCode === (c.plan_code || "custom").toLowerCase() ? "No change" : `Set plan to ${planCode}`}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-slate-950/60 border border-slate-800 rounded p-2 text-xs space-y-1" data-testid="admin-payment-summary">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Status</span>
                  <span className="font-bold">{c.stripe_subscription_status || c.subscription_status || "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Plan</span>
                  <span className="font-bold">{c.plan_code || "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Renews</span>
                  <span className="mono">{(c.subscription_expires_at || "").slice(0, 10) || "—"}</span>
                </div>
              </div>
              <Button
                onClick={sendStripeOnboarding}
                disabled={onboardingBusy}
                size="sm"
                className="mt-2 bg-emerald-700 hover:bg-emerald-600 h-8 text-[11px] w-full"
                data-testid="admin-send-onboarding"
              >
                <Send className="h-3 w-3 mr-1" />
                {onboardingBusy ? "Sending…" : "Send Stripe onboarding email"}
              </Button>
              <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                Charges the full plan price today; next cycle anchors on their current expiry so no manual runway is lost.
              </p>
            </>
          )}
        </section>

        {/* RIGHT COLUMN — Reset password + Metadata */}
        <div className="space-y-3">
          <section className="bg-slate-900 border border-slate-800 rounded-[8px] p-3">
            <div className="text-[10px] uppercase tracking-widest mono text-slate-400 flex items-center gap-1.5 mb-2">
              <KeyRound className="h-3.5 w-3.5" /> Reset manager password
            </div>
            <div className="flex gap-2">
              <Input type="password" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} placeholder="New password (≥6 chars)" className="bg-slate-800 border-slate-700 h-8 text-xs" data-testid="admin-reset-password-input" />
              <Button size="sm" disabled={resetting} onClick={reset} className="bg-amber-700 hover:bg-amber-800 h-8 text-[11px]" data-testid="admin-reset-password-submit">{resetting ? "…" : "Reset"}</Button>
            </div>
          </section>

          <section className="bg-slate-900 border border-slate-800 rounded-[8px] p-3">
            <div className="text-[10px] uppercase tracking-widest mono text-slate-400 mb-2">Metadata</div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Name</Label>
            <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="bg-slate-800 border-slate-700 h-8 text-xs mt-1" data-testid="admin-company-name-input" />
            <Label className="text-[10px] uppercase tracking-wider text-slate-400 mt-2 block">Notes</Label>
            <Textarea rows={2} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className="bg-slate-800 border-slate-700 text-xs mt-1" data-testid="admin-company-notes-input" />
          </section>
        </div>
      </div>

      {/* Discount — full width below the 2-col grid so it stays visible without scroll on 1440×900. */}
      <DiscountCard companyId={id} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-[8px] p-4">
      <div className="mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{label}</div>
      <div className="text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
}


// Phase P — inner panel with Phase-P inline pricing labels + VAT footnote.
// Split out so the Phase O utilization tile stays a pure presentation component
// and the super-admin can see the strike-through Pro offer + Pro Max rename
// without any of that pricing UX polluting the tenant-facing screens.
function PlanPanel({ planCode, setPlanCode, savingPlan, savePlan, savedPlanCode, planCatalog, utilization }) {
  const { catalog } = usePlanDisplay();
  const label = (code) => {
    const name = (catalog[code] || {}).name || code;
    return `${name} · ${priceInline(code, catalog)}`;
  };
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-[8px] p-5" data-testid="admin-plan-panel">
      <h2 className="text-sm uppercase tracking-widest mono text-slate-400 flex items-center gap-2">
        <Briefcase className="h-4 w-4" /> Plan &amp; limits
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        <div className="md:col-span-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400">Plan code</Label>
          <Select value={planCode} onValueChange={setPlanCode}>
            <SelectTrigger className="bg-slate-800 border-slate-700 mt-1" data-testid="admin-plan-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="trial" data-testid="admin-plan-option-trial">{label("trial")}</SelectItem>
              <SelectItem value="basic" data-testid="admin-plan-option-basic">{label("basic")}</SelectItem>
              <SelectItem value="pro" data-testid="admin-plan-option-pro">{label("pro")}</SelectItem>
              <SelectItem value="pro_annual" data-testid="admin-plan-option-pro-annual">{label("pro_annual")}</SelectItem>
              <SelectItem value="pro_unlimited" data-testid="admin-plan-option-pro-unlimited">{label("pro_unlimited")}</SelectItem>
              <SelectItem value="custom" data-testid="admin-plan-option-custom">{label("custom")}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={savePlan}
            disabled={savingPlan || planCode === savedPlanCode}
            className="mt-2 bg-rose-600 hover:bg-rose-700 w-full"
            data-testid="admin-plan-save"
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            {savingPlan ? "Saving…" : planCode === savedPlanCode ? "No change" : `Set to ${planCode}`}
          </Button>
          <VatFootnote className="text-slate-500" />
        </div>
        <PlanUtilizationTile
          planCatalog={planCatalog}
          utilization={utilization}
          selectedPlanCode={planCode}
          currentPlanCode={savedPlanCode}
        />
      </div>
    </section>
  );
}


// Phase O — utilization tile for the super-admin company detail. Renders
// staff + manager counts against the CURRENTLY-SELECTED plan (previewing
// downgrade/upgrade impact live, before Save).
function PlanUtilizationTile({ planCatalog, utilization, selectedPlanCode, currentPlanCode }) {
  if (!planCatalog || !utilization) {
    return (
      <div className="md:col-span-2 bg-slate-950/40 border border-slate-800 rounded-[6px] p-4 text-xs text-slate-500" data-testid="admin-plan-utilization-loading">
        Loading utilization…
      </div>
    );
  }
  const preview = planCatalog[selectedPlanCode] || planCatalog[currentPlanCode] || {};
  const stat = (count, cap) => {
    if (cap === null || cap === undefined) return { label: "unlimited", tone: "text-slate-300", over: false, at: false };
    if (count > cap) return { label: `OVER (+${count - cap})`, tone: "text-rose-300", over: true, at: false };
    if (count === cap) return { label: "AT CAP", tone: "text-amber-300", over: false, at: true };
    return { label: `${cap - count} left`, tone: "text-emerald-300", over: false, at: false };
  };
  const staffStat = stat(utilization.staff_count, preview.max_staff);
  const mgrStat = stat(utilization.manager_total, preview.max_managers);
  const isPreview = selectedPlanCode !== currentPlanCode;
  return (
    <div className="md:col-span-2 bg-slate-950/40 border border-slate-800 rounded-[6px] p-4" data-testid="admin-plan-utilization-tile">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="mono text-[10px] uppercase tracking-widest text-slate-400">
          Utilization vs <span className="text-slate-200 font-bold">{selectedPlanCode}</span>
          {isPreview && <span className="ml-2 text-amber-400">· preview (unsaved)</span>}
        </div>
        <div className="mono text-[10px] text-slate-500">
          features: {(preview.features || []).length ? (preview.features || []).join(", ") : "—"}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="bg-slate-900 border border-slate-800 rounded p-3" data-testid="admin-plan-utilization-staff">
          <div className="flex items-center gap-1.5 text-[10px] mono uppercase tracking-wider text-slate-400">
            <Briefcase className="h-3 w-3" /> Staff
          </div>
          <div className="text-2xl font-black mt-0.5 mono">
            {utilization.staff_count} / {preview.max_staff ?? "∞"}
          </div>
          <div className={`text-[11px] mono uppercase tracking-widest font-bold ${staffStat.tone}`} data-testid="admin-plan-utilization-staff-status">
            {staffStat.label}
          </div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded p-3" data-testid="admin-plan-utilization-managers">
          <div className="flex items-center gap-1.5 text-[10px] mono uppercase tracking-wider text-slate-400">
            <Users className="h-3 w-3" /> Managers
          </div>
          <div className="text-2xl font-black mt-0.5 mono">
            {utilization.manager_total} / {preview.max_managers ?? "∞"}
          </div>
          <div className={`text-[11px] mono uppercase tracking-widest font-bold ${mgrStat.tone}`} data-testid="admin-plan-utilization-managers-status">
            {mgrStat.label}
            {utilization.pending_manager_invites > 0 && (
              <span className="text-slate-500 normal-case font-normal ml-1">· incl. {utilization.pending_manager_invites} pending</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// Phase L — Discount card. Apply, view, remove + live billing preview.
function DiscountCard({ companyId }) {
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ type: "percent", value: "", duration: "once", months: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [d, p] = await Promise.all([
        platformApi.get(`/platform/companies/${companyId}/discount`).then((r) => r.data).catch(() => null),
        platformApi.get(`/platform/companies/${companyId}/billing-preview`).then((r) => r.data).catch(() => null),
      ]);
      setActive(d || null);
      setPreview(p || null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [companyId]);

  const apply = async () => {
    setBusy(true);
    try {
      const payload = {
        type: form.type,
        value: Number(form.value),
        duration: form.duration,
        reason: form.reason || undefined,
      };
      if (form.type === "amount") payload.currency = "EUR";
      if (form.duration === "repeating") payload.duration_in_months = Number(form.months);
      const { data } = await platformApi.post(`/platform/companies/${companyId}/discount`, payload);
      toast.success(`Discount applied${data?.stub ? " (stub mode)" : ""}.`);
      setForm({ type: "percent", value: "", duration: "once", months: "", reason: "" });
      await reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to apply discount");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await platformApi.delete(`/platform/companies/${companyId}/discount`);
      toast.success("Discount removed");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to remove discount");
    } finally {
      setBusy(false);
    }
  };

  const describe = (d) => {
    if (!d) return "—";
    const dur = d.duration === "repeating" ? `for ${d.duration_in_months} months` : d.duration === "forever" ? "forever" : "once";
    return d.type === "percent"
      ? `${Number(d.value).toFixed(0)}% off ${dur}`
      : `€${Number(d.value).toFixed(2)} off ${dur}`;
  };

  return (
    <section
      className="bg-slate-900 border border-slate-800 rounded-[8px] p-5"
      data-testid="superadmin-discount-card"
    >
      <h2 className="text-sm uppercase tracking-widest mono text-slate-400 flex items-center gap-2">
        <BadgePercent className="h-4 w-4" /> Discount
      </h2>
      <p className="text-xs text-slate-500 mt-1">
        Attach a Stripe coupon to this tenant&apos;s SaaS subscription. Used for QA charges
        (e.g. real €1 payment via 99% off) and goodwill credits.
      </p>

      {loading ? (
        <div className="text-xs text-slate-500 mt-3">Loading…</div>
      ) : active ? (
        <div className="mt-3 space-y-3" data-testid="discount-active-summary">
          <div className="bg-amber-950/40 border border-amber-800 text-amber-200 rounded-[6px] p-3">
            <div className="text-lg font-extrabold">{describe(active)}</div>
            <div className="text-[11px] mono uppercase tracking-widest text-amber-300/70 mt-1">
              applied by {active.applied_by_email || "—"} · {(active.applied_at || "").slice(0, 19).replace("T", " ")}
            </div>
            {active.reason && <div className="text-xs italic mt-1">&ldquo;{active.reason}&rdquo;</div>}
            {active.stub && <div className="text-[10px] mono uppercase text-amber-400/70 mt-1">stub mode — no live Stripe coupon</div>}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={remove}
            disabled={busy}
            className="border-rose-700 text-rose-300 hover:bg-rose-950"
            data-testid="discount-remove-btn"
          >
            <X className="h-3.5 w-3.5 mr-1" /> Remove discount
          </Button>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Type</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, type: "percent" })}
                className={`flex-1 px-3 py-1.5 rounded border text-xs font-bold flex items-center justify-center gap-1 ${form.type === "percent" ? "bg-sky-900 border-sky-700 text-sky-100" : "bg-slate-800 border-slate-700 text-slate-400"}`}
                data-testid="discount-type-percent"
              ><Percent className="h-3 w-3" /> Percentage</button>
              <button
                type="button"
                onClick={() => setForm({ ...form, type: "amount" })}
                className={`flex-1 px-3 py-1.5 rounded border text-xs font-bold flex items-center justify-center gap-1 ${form.type === "amount" ? "bg-sky-900 border-sky-700 text-sky-100" : "bg-slate-800 border-slate-700 text-slate-400"}`}
                data-testid="discount-type-amount"
              ><Euro className="h-3 w-3" /> Fixed €</button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">
              {form.type === "percent" ? "% off (1-100)" : "€ off (>0)"}
            </Label>
            <Input
              type="number"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              placeholder={form.type === "percent" ? "99" : "10.00"}
              className="bg-slate-800 border-slate-700"
              data-testid="discount-value-input"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Duration</Label>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setForm({ ...form, duration: "once" })}
                className={`px-3 py-1.5 rounded border text-xs font-bold ${form.duration === "once" ? "bg-sky-900 border-sky-700 text-sky-100" : "bg-slate-800 border-slate-700 text-slate-400"}`}
                data-testid="discount-duration-once"
              >One-off</button>
              <button
                type="button"
                onClick={() => setForm({ ...form, duration: "forever" })}
                className={`px-3 py-1.5 rounded border text-xs font-bold ${form.duration === "forever" ? "bg-sky-900 border-sky-700 text-sky-100" : "bg-slate-800 border-slate-700 text-slate-400"}`}
                data-testid="discount-duration-forever"
              >Forever</button>
              <button
                type="button"
                onClick={() => setForm({ ...form, duration: "repeating" })}
                className={`px-3 py-1.5 rounded border text-xs font-bold ${form.duration === "repeating" ? "bg-sky-900 border-sky-700 text-sky-100" : "bg-slate-800 border-slate-700 text-slate-400"}`}
                data-testid="discount-duration-repeating"
              >Repeating</button>
              {form.duration === "repeating" && (
                <Input
                  type="number"
                  value={form.months}
                  onChange={(e) => setForm({ ...form, months: e.target.value })}
                  placeholder="Months"
                  className="bg-slate-800 border-slate-700 max-w-[120px]"
                  data-testid="discount-duration-months"
                />
              )}
            </div>
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Reason (optional)</Label>
            <Input
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. real €1 payment QA"
              className="bg-slate-800 border-slate-700"
              data-testid="discount-reason-input"
            />
          </div>
          <div className="md:col-span-2">
            <Button
              type="button"
              onClick={apply}
              disabled={busy || !form.value || (form.duration === "repeating" && !form.months)}
              className="bg-sky-700 hover:bg-sky-800"
              data-testid="discount-apply-btn"
            >
              {busy ? "Applying…" : "Apply discount"}
            </Button>
          </div>
        </div>
      )}

      {/* Billing preview */}
      {preview && (
        <div className="mt-4 bg-slate-950 border border-slate-800 rounded-[6px] p-3">
          <div className="text-[10px] mono uppercase tracking-widest text-slate-400">
            Next invoice preview{preview.stub ? " · stub" : ""}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-2 text-sm">
            <div>
              <div className="text-[10px] text-slate-500">Subtotal</div>
              <div className="font-bold mono tabular-nums" data-testid="billing-preview-subtotal">
                €{Number(preview.subtotal || 0).toFixed(2)}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500">Discount</div>
              <div className="font-bold mono tabular-nums text-emerald-400" data-testid="billing-preview-discount">
                -€{Number(preview.discount_amount || 0).toFixed(2)}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500">Total</div>
              <div className="font-extrabold mono tabular-nums text-sky-300" data-testid="billing-preview-total">
                €{Number(preview.total || 0).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
